/**
 * HashDisplay — monospace truncation with click-to-copy + a tooltip
 * showing the full value. Accessible: announces the full hash on
 * keyboard focus via `aria-label`.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { copyToClipboard, truncateHash } from '@/utils/format';
import { cn } from '@/utils/cn';

export function HashDisplay({ value, className }: { value: string; className?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={`${value} — ${t('actions.copy')}`}
      title={value}
      className={cn(
        'inline-flex items-center gap-1 font-mono text-xs text-ink-muted',
        'hover:text-ink transition-colors',
        className,
      )}
      data-mono
    >
      <span>{truncateHash(value)}</span>
      <span className="text-[10px] text-ink-subtle" aria-live="polite">
        {copied ? t('actions.copied') : ''}
      </span>
    </button>
  );
}
